<template>
  <div class="datepicker-wrapper" :class="{ '-error': hasError }">
    <FieldLabel
      :id="fieldConfig && fieldConfig.id"
      :label="fieldConfig && fieldConfig.label"
      :description="fieldConfig && fieldConfig.description"
      :required="fieldConfig && fieldConfig.required"
    />
    <input
      :id="fieldConfig && fieldConfig.id"
      ref="input"
      type="text"
      class="field-input"
      :placeholder="fieldConfig && fieldConfig.placeholder"
      :disabled="fieldConfig && fieldConfig.disabled"
      :required="fieldConfig && fieldConfig.required"
      :aria-invalid="hasError ? 'true' : 'false'"
    />
    <label
      v-if="hasError"
      :for="fieldConfig && fieldConfig.id"
      class="field-error"
    >
      {{ errorMessage }}
    </label>
  </div>
</template>

<script>
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";
import FieldLabel from "../FieldLabel/FieldLabel.vue";
import errorDisplay from "../mixins/errorDisplay.js";
import nativeChangeGuard from "../mixins/nativeChangeGuard.js";
import vModelHelpers from "../mixins/vModelHelpers.js";

export default {
  name: "DatepickerField",
  components: {
    FieldLabel,
  },
  mixins: [errorDisplay, nativeChangeGuard, vModelHelpers],
  props: {
    fieldConfig: {
      type: Object,
      default: () => ({
        id: "datepicker",
        label: "",
        placeholder: "Select date",
        disabled: false,
        required: false,
        minDate: null,
        maxDate: null,
        format: undefined,
        displayFormat: undefined,
      }),
    },
    value: {
      type: [Date, String, Array],
      default: null,
    },
    modelValue: {
      type: [Date, String, Array],
      default: undefined,
    },
  },
  emits: ["input", "update:modelValue", "change", "dateChange"],
  data() {
    return {
      flatpickrInstance: null,
      internalValue: this.initialFromModel(this.modelValue, this.value, null),
    };
  },
  computed: {
    flatpickrConfig() {
      const flatpickrOptions = {
        minDate: this.fieldConfig && this.fieldConfig.minDate,
        maxDate: this.fieldConfig && this.fieldConfig.maxDate,
        defaultDate: this.internalValue,
      };
      const fieldConfigLocal = this.fieldConfig || {};
      const jsonFormat = fieldConfigLocal && fieldConfigLocal.format;
      const jsonDisplay = fieldConfigLocal && fieldConfigLocal.displayFormat;
      if (jsonFormat) flatpickrOptions.dateFormat = jsonFormat;
      if (jsonDisplay) {
        flatpickrOptions.altInput = true;
        flatpickrOptions.altFormat = jsonDisplay;
      }
      return flatpickrOptions;
    },
  },
  watch: {
    value(newValue) {
      if (this.modelValue !== undefined) return;
      this.internalValue = newValue;
      if (this.flatpickrInstance)
        this.flatpickrInstance.setDate(newValue, false);
    },
    modelValue(newValue) {
      this.internalValue = newValue;
      if (this.flatpickrInstance)
        this.flatpickrInstance.setDate(newValue, false);
    },
    fieldConfig: {
      handler() {
        if (this.flatpickrInstance) {
          const minDate = this.fieldConfig && this.fieldConfig.minDate;
          const maxDate = this.fieldConfig && this.fieldConfig.maxDate;
          this.flatpickrInstance.set("minDate", minDate);
          this.flatpickrInstance.set("maxDate", maxDate);
          if (this.fieldConfig && this.fieldConfig.format)
            this.flatpickrInstance.set("dateFormat", this.fieldConfig.format);
          if (this.fieldConfig && this.fieldConfig.displayFormat) {
            this.flatpickrInstance.set(
              "altFormat",
              this.fieldConfig.displayFormat,
            );
          }
          if (this.flatpickrInstance && this.flatpickrInstance.redraw)
            this.flatpickrInstance.redraw();
          const v = this.internalValue;
          const tooEarly = minDate && v && typeof v === "string" && v < minDate;
          const tooLate = maxDate && v && typeof v === "string" && v > maxDate;
          if (tooEarly || tooLate) {
            this.internalValue = null;
            this.flatpickrInstance.clear();
            this.$emit("input", null);
            this.$emit("update:modelValue", null);
            this.$emit("change", null);
            this.$emit("dateChange", null);
          }
        }
        if (this.$refs.input) {
          this.$refs.input.disabled = !!(
            this.fieldConfig && this.fieldConfig.disabled
          );
          this.$refs.input.required = !!(
            this.fieldConfig && this.fieldConfig.required
          );
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.flatpickrInstance = flatpickr(this.$refs.input, {
      ...this.flatpickrConfig,
      onChange: this.onChange,
    });
    if (this.$refs && this.$refs.input) {
      this.onNativeChangeHandler = (event) => {
        this.stopNativeChange(event);
      };
      this.$refs.input.addEventListener("change", this.onNativeChangeHandler);
    }
  },
  beforeUnmount() {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
      this.flatpickrInstance = null;
    }
    if (this.$refs && this.$refs.input && this.onNativeChangeHandler) {
      this.$refs.input.removeEventListener(
        "change",
        this.onNativeChangeHandler,
      );
      this.onNativeChangeHandler = null;
    }
  },
  methods: {
    onChange(selectedDates, dateStr) {
      const previousValue = this.internalValue;
      const previousStringValue = Array.isArray(previousValue)
        ? previousValue.join("|")
        : previousValue !== undefined && previousValue !== null
        ? String(previousValue)
        : "";
      const newValue = Array.isArray(selectedDates)
        ? selectedDates
        : dateStr || (selectedDates && selectedDates[0]) || null;
      let newStringValue = "";
      if (typeof dateStr === "string" && dateStr) {
        newStringValue = dateStr;
      } else if (this.flatpickrInstance && selectedDates && selectedDates[0]) {
        newStringValue = this.flatpickrInstance.formatDate(
          selectedDates[0],
          this.flatpickrInstance.config &&
            this.flatpickrInstance.config.dateFormat,
        );
      }
      if (newStringValue === previousStringValue) {
        return;
      }
      this.internalValue = newValue;
      this.emitModel(newValue);
      this.$emit("dateChange", newValue);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/abstracts/mixins";
@import "../styles/field-shared";

.datepicker-wrapper {
  position: relative;
  width: 100%;
}

/* Flatpickr creates its own input when altInput is enabled. Use deep selectors to style it. */
:deep(input.flatpickr-input:not([type="hidden"])),
:deep(input.form-control.input) {
  width: 100%;
  padding: 12px 14px;

  color: var(--cr-form-field-text-color, inherit);

  background: var(--cr-form-field-background, #fff);
  border: 1px solid var(--cr-form-field-border-color, #e5e7eb);
  border-radius: 6px;

  @include font-family("body");
  @include scale-fonts("body");
}

/* Error state for flatpickr's mirrored input */
.datepicker-wrapper.-error :deep(input.flatpickr-input:not([type="hidden"])),
.datepicker-wrapper.-error :deep(input.form-control.input) {
  border-color: var(--cr-form-error-color, #d00);
}

:deep(input.flatpickr-input:not([type="hidden"]))::placeholder,
:deep(input.form-control.input::placeholder) {
  color: var(--cr-form-placeholder-color, #9ca3af);
}
</style>

<style lang="scss">
@import "../../../assets/scss/abstracts/mixins";

/* Flatpickr appends the calendar to <body>, so scoped styles won't reach it. */
.flatpickr-calendar,
.flatpickr-calendar .flatpickr-months,
.flatpickr-calendar .flatpickr-months .flatpickr-month,
.flatpickr-calendar .flatpickr-weekdays,
.flatpickr-calendar .flatpickr-weekday,
.flatpickr-calendar .flatpickr-days,
.flatpickr-calendar .flatpickr-day {
  @include font-family("body");
}
</style>
